import {
  Checkbox,
  Dialog,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Vessel } from "../../common/Vessel";
import { useState } from "react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { Edit, Delete, RestoreFromTrash } from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { useCreateTrainingLocationMutation, useCreateTrainingMutation, useDeleteTrainingLocationMutation, useDeleteTrainingMutation, useEditTrainingMutation, useGetTrainingLocationsQuery, useGetTrainingsQuery, useRestoreTrainingMutation } from "../../redux/api/training";
import { Training, trainingCreateParams } from "../../common/Training";
import { useCreateComplianceTrainingMutation, useDeleteComplianceTrainingMutation, useListComplianceTrainingsQuery, useRestoreComplianceTrainingMutation, useUpdateComplianceTrainingMutation } from "../../redux/api/complianceTraining";
import { ComplianceTraining } from "../../common/ComplianceTraining";
import useDocumentTitle from "../../util/useDocumentTitle";
import UnionHall from "./UnionHall";
import PortCreate from "./PortCreate";



const AdminPage = (props: {
  vessels?: Vessel[];
  onClose?: (bol: boolean) => void;
}) => {
  useDocumentTitle("Admin Page");

  const [trainingLocation, setTrainingLocation] = useState('');
  const [deleteTrainingLocation, setDeleteTrainingLocation] = useState('');
  const [createTrainingLocation] = useCreateTrainingLocationMutation();
  const { data: locations, isLoading: isLocationLoading, isFetching: isLocationFetching, refetch: refetchLocations } = useGetTrainingLocationsQuery();
  const { data: trainings, isLoading: isTrainingLoading, isFetching: isTrainingFetching, refetch: refetchTrainings } = useGetTrainingsQuery(true);
  const [deleteLocation] = useDeleteTrainingLocationMutation();
  const [createTraining] = useCreateTrainingMutation();
  const [updateTraining] = useEditTrainingMutation();
  const [deleteTraining] = useDeleteTrainingMutation();
  const [restoreTraining] = useRestoreTrainingMutation();
  const [confirmDeleteTraining, setConfirmDeleteTraining] = useState(false);
  const [confirmRestoreTraining, setConfirmRestoreTraining] = useState(false);

  const handleAddLocation = () => {
    createTrainingLocation(trainingLocation).then((response) => {
      if (!("error" in response)) {

        refetchLocations();
        toast.success(<p>Training Location created successfully</p>)
        setTrainingLocation('');
      }
    })
  }
  const handleDeleteLocation = async () => {
    const response = await deleteLocation(deleteTrainingLocation);
    if ("error" in response === false) {
      refetchLocations();
      toast.success(<p>Location deleted successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      })
    } else {
      toast.error(<p>A training location must be selected.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  }

  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);
  const [trainingName, setTrainingName] = useState('');
  const [validity, setValidity] = useState(0);
  const [addTraining, setAddTraining] = useState(false);
  const [editTraining, setEditTraining] = useState(false);

  const handleAddTrainingOpen = () => {
    setSelectedTraining(null);
    setTrainingName('');
    setValidity(0);
    setAddTraining(true);
  }

  const handleAddTrainingClose = () => {
    setAddTraining(false);
    setSelectedTraining(null);
  }

  const handleAddTraining = async () => {
    const trainingCreateParams: trainingCreateParams = {
      name: trainingName ? trainingName : '',
      validityInMonths: validity ? +validity : 0,
    };
    createTraining(trainingCreateParams).then((response) => {
      if (!("error" in response)) {
        refetchTrainings();
        setAddTraining(false);
        toast.success(<p>Training created successfully.</p>);
      }
    });
  }

  const handleTrainingOpen = (training: Training) => {
    setSelectedTraining(training);
    setTrainingName(training.name ? training.name : '');
    setValidity(training?.validityInMonths ? training.validityInMonths : 0)
    setEditTraining(true);
  }

  const handleTrainingClose = () => {
    setSelectedTraining(null);
    setEditTraining(false);
  }

  const handleEditTraining = async () => {
    const currTraining: Training = {
      id: selectedTraining?.id,
      deleted: selectedTraining?.deleted,
      name: trainingName,
      validityInMonths: validity,
    }
    const response = await updateTraining({
      trainingId: currTraining.id!,
      training: currTraining
    })
    if (!("error" in response)) {
      refetchTrainings();
      setEditTraining(false);
      toast.success(<p>Training updated successfully</p>);
    }
  }

  const handleDeleteTrainingClose = () => {
    setConfirmDeleteTraining(false);
  }

  const handleDeleteTrainingOpen = (training: Training) => {
    setSelectedTraining(training);
    setConfirmDeleteTraining(true);
  };

  const handleDeleteTraining = async () => {
    const response = await deleteTraining(selectedTraining!.id!)
    if (!("error" in response)) {
      refetchTrainings();
      setConfirmDeleteTraining(false);
      toast.success(<p>Training deleted successfully</p>)
    }
  }

  const handleRestoreTrainingOpen = (training: Training) => {
    setSelectedTraining(training);
    setConfirmRestoreTraining(true);
  }

  const handleRestoreTrainingClose = () => {
    setConfirmRestoreTraining(false);
  }

  const handleRestoreTraining = async () => {
    const response = await restoreTraining(selectedTraining!.id!)
    if (!("error" in response)) {
      refetchTrainings();
      setConfirmRestoreTraining(false);
      toast.success(<p>Training restored successfully</p>)
    }
  }

  const { data: complianceTrainings, refetch: refetchComplianceTrainings } = useListComplianceTrainingsQuery(true);
  const [createComplianceTraining] = useCreateComplianceTrainingMutation();
  const [updateComplianceTraining] = useUpdateComplianceTrainingMutation();
  const [deleteComplianceTraining] = useDeleteComplianceTrainingMutation();
  const [restoreComplianceTraining] = useRestoreComplianceTrainingMutation();

  const [selectedComplianceTraining, setSelectedComplianceTraining] = useState<ComplianceTraining | null>(null);
  const [complianceTrainingName, setComplianceTrainingName] = useState('');
  const [complianceTrainingValidity, setComplianceTrainingValidity] = useState<number>(0);
  const [complianceTrainingDocumentRequired, setComplianceTrainingDocumentRequired] = useState(true);
  const [addComplianceTraining, setAddComplianceTraining] = useState(false);
  const [editComplianceTraining, setEditComplianceTraining] = useState(false);
  const [confirmDeleteComplianceTraining, setConfirmDeleteComplianceTraining] = useState(false);
  const [confirmRestoreComplianceTraining, setConfirmRestoreComplianceTraining] = useState(false);

  const handleAddComplianceTrainingOpen = () => {
    setSelectedComplianceTraining(null);
    setComplianceTrainingName('');
    setComplianceTrainingValidity(0);
    setComplianceTrainingDocumentRequired(true);
    setAddComplianceTraining(true);
  };

  const handleAddComplianceTrainingClose = () => {
    setAddComplianceTraining(false);
    setSelectedComplianceTraining(null);
  };

  const handleAddComplianceTraining = async () => {
    const response = await createComplianceTraining({
      name: complianceTrainingName,
      validityInMonths: complianceTrainingValidity || null,
      documentRequired: complianceTrainingDocumentRequired,
    });
    if (!("error" in response)) {
      refetchComplianceTrainings();
      setAddComplianceTraining(false);
      toast.success(<p>Compliance training created successfully.</p>);
    }
  };

  const handleComplianceTrainingOpen = (training: ComplianceTraining) => {
    setSelectedComplianceTraining(training);
    setComplianceTrainingName(training.name ?? '');
    setComplianceTrainingValidity(training.validityInMonths ?? 0);
    setComplianceTrainingDocumentRequired(training.documentRequired);
    setEditComplianceTraining(true);
  };

  const handleComplianceTrainingClose = () => {
    setSelectedComplianceTraining(null);
    setEditComplianceTraining(false);
  };

  const handleEditComplianceTraining = async () => {
    const response = await updateComplianceTraining({
      id: selectedComplianceTraining!.id,
      name: complianceTrainingName,
      validityInMonths: complianceTrainingValidity || null,
      documentRequired: complianceTrainingDocumentRequired,
    });
    if (!("error" in response)) {
      refetchComplianceTrainings();
      setEditComplianceTraining(false);
      toast.success(<p>Compliance training updated successfully.</p>);
    }
  };

  const handleDeleteComplianceTrainingOpen = (training: ComplianceTraining) => {
    setSelectedComplianceTraining(training);
    setConfirmDeleteComplianceTraining(true);
  };

  const handleDeleteComplianceTrainingClose = () => {
    setConfirmDeleteComplianceTraining(false);
  };

  const handleDeleteComplianceTraining = async () => {
    const response = await deleteComplianceTraining(selectedComplianceTraining!.id);
    if (!("error" in response)) {
      refetchComplianceTrainings();
      setConfirmDeleteComplianceTraining(false);
      toast.success(<p>Compliance training deleted successfully.</p>);
    }
  };

  const handleRestoreComplianceTrainingOpen = (training: ComplianceTraining) => {
    setSelectedComplianceTraining(training);
    setConfirmRestoreComplianceTraining(true);
  };

  const handleRestoreComplianceTrainingClose = () => {
    setConfirmRestoreComplianceTraining(false);
  };

  const handleRestoreComplianceTraining = async () => {
    const response = await restoreComplianceTraining(selectedComplianceTraining!.id);
    if (!("error" in response)) {
      refetchComplianceTrainings();
      setConfirmRestoreComplianceTraining(false);
      toast.success(<p>Compliance training restored successfully.</p>);
    }
  };

  return (
    <div>
      <PortCreate />
      <UnionHall />
      <div className="flex align-items-center g-3" style={{ marginTop: '60px' }}>
        <h2>Add Training Location</h2>
      </div>
      <div className="flex-col g-2" style={{ marginTop: '10px' }}>
        <div className="flex-col g-0">
          <TextField
            className="wd-300"
            placeholder="Type Location"
            onChange={event => {
              setTrainingLocation(event.target.value)
            }}
            value={trainingLocation}
          />
          <div className="flex g-2" style={{ marginTop: '10px' }}>
            <Button
              className="blue-button"
              onClick={handleAddLocation}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
      <div className="flex align-items-center g-3" style={{ marginTop: '60px' }}>
        <h2>Delete Training Location</h2>
      </div>
      <div className="flex-col g-0" style={{ marginTop: '10px' }}>
        {!isLocationLoading && !isLocationFetching && locations &&
          <Select
            id="deleteLocation"
            className="wd-300"
            size="medium"
            defaultValue=""
            input={<OutlinedInput />}
            onChange={(event) =>
              setDeleteTrainingLocation(event.target.value)
            }
          >
            {Object.values(locations).map((location) => (
              <MenuItem key={location.id} value={location.name}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        }
        <div className="flex g-2">
          <Button
            className="grey-link-button"
            onClick={handleDeleteLocation}
          >
            <Delete />
            Delete
          </Button>
        </div>

      </div>
      <div className="flex align-items-center g-3" style={{ marginTop: '60px' }}>
        <h2>Edit Training</h2>
        <Button
          variant="contained"
          className="blue-button"
          type="submit"
          onClick={handleAddTrainingOpen}
        >
          Add New
        </Button>
      </div>
      <TableContainer className="flex wd-50">
        <Table className="table ">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '600px' }}>
                Training Name
              </TableCell>
              <TableCell style={{ width: '150px' }}>
                Validity (months)
              </TableCell>
              <TableCell className="wd-100px">
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isTrainingLoading && !isTrainingFetching && trainings?.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>
                  <span style={row.deleted ? { color: 'red' } : undefined}>{row.name}</span>
                </TableCell>
                <TableCell>
                  <span style={row.deleted ? { color: 'red' } : undefined}>{row.validityInMonths}</span>
                </TableCell>
                <TableCell>
                  {row.deleted ? (
                    <div className="flex justify-content-center align-items-center g-3">
                      <NavLink
                        to=""
                        className="blue-link"
                        onClick={() => handleRestoreTrainingOpen(row)}
                      >
                        <RestoreFromTrash color="error" />
                      </NavLink>
                    </div>
                  ) : (
                    <div className="flex justify-content-center align-items-center g-3">
                      <div className="flex justify-content-left">
                        <NavLink
                          to=""
                          className="blue-link"
                          onClick={() => handleTrainingOpen(row)}
                        >
                          <Edit color="primary" />
                        </NavLink>
                      </div>
                      <div className="flex justify-content-left">
                        <NavLink
                          to=""
                          className="blue-link"
                          onClick={() => handleDeleteTrainingOpen(row)}
                        >
                          <Delete color="primary" />
                        </NavLink>
                      </div>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={addTraining}
        onClose={handleAddTrainingClose}
        aria-labelledby="modal-title"
      >
        <TableContainer className="flex wd-50">
          <Table className="table">
            <TableRow>
              <TableCell>
                Name
              </TableCell>
              <TableCell>
                <TextField
                  name="training"
                  style={{ width:'400px' }}
                  value={trainingName}
                  onChange={(e) => {
                    setTrainingName(e.target.value);
                  }}
                >

                </TextField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Validity (months)
              </TableCell>
              <TableCell>
                <TextField
                  name="validity"
                  type="number"
                  className="wd-100px"
                  value={validity}
                  onChange={(e) => {
                    setValidity(+e.target.value);
                  }}
                >

                </TextField>
              </TableCell>
            </TableRow>
          </Table>
        </TableContainer>
        <div className="modal-footer">
          <Button
            variant="contained"
            className="blue-button"
            type="submit"
            onClick={handleAddTraining}
          >
            Save
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={editTraining}
        onClose={handleTrainingClose}
      // aria-labelledby="modal-title"
      >
        <Table className="table">
          <TableRow>
            <TableCell>
              Training Name
            </TableCell>
            <TableCell>
              <TextField
                name="trainingName"
                style={{ width:'400px' }}
                value={trainingName}
                onChange={(e) => {
                  setTrainingName(e.target.value)
                }}
              >
              </TextField>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Validity (months)
            </TableCell>
            <TableCell>
              <TextField
                name="validity"
                type="number"
                className="wd-100px"
                value={validity}
                onChange={(e) => {
                  setValidity(+e.target.value)
                }}
              >
              </TextField>
            </TableCell>
          </TableRow>
        </Table>
        <div className="modal-footer">
          <Button
            variant="contained"
            className="blue-button"
            type="submit"
            onClick={handleEditTraining}
          >
            Save
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={confirmDeleteTraining}
        onClose={handleDeleteTrainingClose}
        aria-labelledby="modal-title"
      >
        <Typography component="h2" id="modal-title">
          Delete Training
        </Typography>
        <br />
        <p>Are you sure you would like to delete the training <b>{selectedTraining?.name}</b>?</p>
        <br />
        <p>This will hide all related training orders and documents within the application, including those still pending individual confirmation, completion, or deletion.</p>
        <div className="modal-footer">
          <Button
            className="blue-button-small"
            onClick={handleDeleteTraining}
          >
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={handleDeleteTrainingClose}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={confirmRestoreTraining}
        onClose={handleRestoreTrainingClose}
        aria-labelledby="modal-title"
      >
        <Typography component="h2" id="modal-title">
          Restore Training
        </Typography>
        <br />
        <p>Are you sure you would like to restore the training <b>{selectedTraining?.name}</b>?</p>
        <br />
        <p>This will restore all related training orders and documents within the application that have not been manually deleted elsewhere, including those which may now be considered expired.</p>
        <div className="modal-footer">
          <Button
            className="blue-button-small"
            onClick={handleRestoreTraining}
          >
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={handleRestoreTrainingClose}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
      <div className="flex align-items-center g-3" style={{ marginTop: '60px' }}>
        <h2>Edit Compliance Training</h2>
        <Button
          variant="contained"
          className="blue-button"
          type="submit"
          onClick={handleAddComplianceTrainingOpen}
        >
          Add New
        </Button>
      </div>
      <TableContainer className="flex wd-50">
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '500px' }}>
                Training Name
              </TableCell>
              <TableCell style={{ width: '150px' }}>
                Validity (months)
              </TableCell>
              <TableCell style={{ width: '150px' }}>
                Doc Required
              </TableCell>
              <TableCell className="wd-100px">
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {complianceTrainings?.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <span style={row.deleted ? { color: 'red' } : undefined}>{row.name}</span>
                </TableCell>
                <TableCell>
                  <span style={row.deleted ? { color: 'red' } : undefined}>{row.validityInMonths ?? ''}</span>
                </TableCell>
                <TableCell>
                  <span style={row.deleted ? { color: 'red' } : undefined}>{row.documentRequired ? 'Yes' : 'No'}</span>
                </TableCell>
                <TableCell>
                  {row.deleted ? (
                    <div className="flex justify-content-center align-items-center g-3">
                      <NavLink
                        to=""
                        className="blue-link"
                        onClick={() => handleRestoreComplianceTrainingOpen(row)}
                      >
                        <RestoreFromTrash color="error" />
                      </NavLink>
                    </div>
                  ) : (
                    <div className="flex justify-content-center align-items-center g-3">
                      <div className="flex justify-content-left">
                        <NavLink
                          to=""
                          className="blue-link"
                          onClick={() => handleComplianceTrainingOpen(row)}
                        >
                          <Edit color="primary" />
                        </NavLink>
                      </div>
                      <div className="flex justify-content-left">
                        <NavLink
                          to=""
                          className="blue-link"
                          onClick={() => handleDeleteComplianceTrainingOpen(row)}
                        >
                          <Delete color="primary" />
                        </NavLink>
                      </div>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={addComplianceTraining}
        onClose={handleAddComplianceTrainingClose}
        aria-labelledby="modal-title"
      >
        <TableContainer className="flex wd-50">
          <Table className="table">
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>
                <TextField
                  name="complianceTrainingName"
                  style={{ width: '400px' }}
                  value={complianceTrainingName}
                  onChange={(e) => setComplianceTrainingName(e.target.value)}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Validity (months)</TableCell>
              <TableCell>
                <TextField
                  name="complianceTrainingValidity"
                  type="number"
                  className="wd-100px"
                  value={complianceTrainingValidity || ''}
                  onChange={(e) => setComplianceTrainingValidity(+e.target.value)}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Document Required</TableCell>
              <TableCell>
                <Checkbox
                  checked={complianceTrainingDocumentRequired}
                  onChange={(e) => setComplianceTrainingDocumentRequired(e.target.checked)}
                />
              </TableCell>
            </TableRow>
          </Table>
        </TableContainer>
        <div className="modal-footer">
          <Button
            variant="contained"
            className="blue-button"
            type="submit"
            onClick={handleAddComplianceTraining}
          >
            Save
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={editComplianceTraining}
        onClose={handleComplianceTrainingClose}
      >
        <Table className="table">
          <TableRow>
            <TableCell>Training Name</TableCell>
            <TableCell>
              <TextField
                name="complianceTrainingName"
                style={{ width: '400px' }}
                value={complianceTrainingName}
                onChange={(e) => setComplianceTrainingName(e.target.value)}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Validity (months)</TableCell>
            <TableCell>
              <TextField
                name="complianceTrainingValidity"
                type="number"
                className="wd-100px"
                value={complianceTrainingValidity || ''}
                onChange={(e) => setComplianceTrainingValidity(+e.target.value)}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Document Required</TableCell>
            <TableCell>
              <Checkbox
                checked={complianceTrainingDocumentRequired}
                onChange={(e) => setComplianceTrainingDocumentRequired(e.target.checked)}
              />
            </TableCell>
          </TableRow>
        </Table>
        <div className="modal-footer">
          <Button
            variant="contained"
            className="blue-button"
            type="submit"
            onClick={handleEditComplianceTraining}
          >
            Save
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={confirmDeleteComplianceTraining}
        onClose={handleDeleteComplianceTrainingClose}
        aria-labelledby="modal-title"
      >
        <Typography component="h2" id="modal-title">
          Delete Compliance Training
        </Typography>
        <br />
        <p>Are you sure you would like to delete the compliance training <b>{selectedComplianceTraining?.name}</b>?</p>
        <br />
        <p>This will hide it from the roster and upload modal. Existing records are preserved.</p>
        <div className="modal-footer">
          <Button
            className="blue-button-small"
            onClick={handleDeleteComplianceTraining}
          >
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={handleDeleteComplianceTrainingClose}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={confirmRestoreComplianceTraining}
        onClose={handleRestoreComplianceTrainingClose}
        aria-labelledby="modal-title"
      >
        <Typography component="h2" id="modal-title">
          Restore Compliance Training
        </Typography>
        <br />
        <p>Are you sure you would like to restore the compliance training <b>{selectedComplianceTraining?.name}</b>?</p>
        <br />
        <p>This will make it visible again on the roster and upload modal.</p>
        <div className="modal-footer">
          <Button
            className="blue-button-small"
            onClick={handleRestoreComplianceTraining}
          >
            Confirm
          </Button>
          <Button
            className="outline-button-small"
            onClick={handleRestoreComplianceTrainingClose}
          >
            Cancel
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default AdminPage;
